// frontend/src/pages/LoginPage.tsx
import { useState, type FormEvent, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { loginUser } from '@/api/authService';
import { toast } from 'sonner';
import { jwtDecode } from 'jwt-decode';
import { type DecodedTokenPayload } from '@/types/authTypes'; 
import { Loader2, LogIn, Mail, Lock, CheckCircle } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Check for state passed from the registration page
  useEffect(() => {
    if (location.state?.fromRegistration) {
      setRegistrationSuccess(true);
      // Clear the state to prevent the message from showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null); // Clear previous errors
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await loginUser(email, password);
      const { access_token } = response.data;

      localStorage.setItem('authToken', access_token);

      try {
        const decodedToken = jwtDecode<DecodedTokenPayload>(access_token);
        localStorage.setItem('userRole', decodedToken.role);
        if (decodedToken.name) {
          localStorage.setItem('userName', decodedToken.name);
        }
        
        // Announce storage change to other tabs/windows
        window.dispatchEvent(new Event('storage'));

        toast.success('Login successful! Redirecting...');

        if (decodedToken.role === 'admin') {
          navigate('/admin/dashboard');
        } else {
          navigate('/');
        }
      } catch (decodeError) {
        console.error("Failed to decode token:", decodeError);
        setError("Login failed: Invalid token received from server.");
        localStorage.clear();
      }

    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'An unexpected error occurred. Please try again.';
      setError(errorMsg);
      toast.error(`Login failed: ${errorMsg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] md:min-h-[calc(100vh-10rem)] bg-gradient-to-br from-background via-muted/30 to-background dark:from-background dark:via-muted/10 dark:to-background py-12 px-4 sm:px-6 lg:px-8 text-foreground">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <LogIn className="mx-auto h-14 w-14 text-primary" />
          </Link>
          <h1 className="mt-6 text-4xl font-bold tracking-tight text-gradient-maroon-to-gold">
            Sign In to Your Account
          </h1>
        </div>

        {registrationSuccess && (
          <div className="bg-accent/10 border border-accent/30 text-accent dark:text-accent p-4 rounded-md flex items-center space-x-3 mb-6">
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">Registration successful! Please sign in with your new account.</p>
          </div>
        )}

        <Card className="bg-card text-card-foreground shadow-2xl border border-border/30 dark:border-border/20">
          <CardHeader className="pt-6 pb-2">
            <CardDescription className="text-center text-muted-foreground">
              Enter your credentials below to access your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="sr-only">Email address</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Mail className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  </div>
                  <Input
                    id="email" name="email" type="email" autoComplete="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-border focus:border-primary focus:ring-primary"
                    placeholder="Email address" disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="sr-only">Password</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Lock className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  </div>
                  <Input
                    id="password" name="password" type="password" autoComplete="current-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 border-border focus:border-primary focus:ring-primary"
                    placeholder="Password" disabled={isSubmitting}
                  />
                </div>
              </div>

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md border border-destructive/30">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-end text-sm">
                <div className="font-medium text-primary hover:text-accent dark:text-accent dark:hover:text-primary transition-colors">
                  <Link to="/forgot-password">Forgot your password?</Link>
                </div>
              </div>

              <div>
                <Button type="submit" className="w-full !bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-md hover:shadow-lg transition-all cursor-pointer" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In <LogIn className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        <div className="text-center text-sm text-muted-foreground">
          Need an account?{' '}
          <Link to="/register" className="font-medium text-primary hover:text-accent dark:text-accent dark:hover:text-primary transition-colors">
            Sign Up Here
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
